// src/components/HeaderComponent.js
import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  Image,
  Animated,
  TouchableOpacity,
  Modal,
  Text as RNText,
  ScrollView,
  Alert,
  Platform,
  TextInput,
} from 'react-native';
import { Title, Text, IconButton, Badge, Divider, List } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { AVAQualisTheme } from '../theme/theme';
import { getLevelEmoji, getLevelDescription, AVALIADORES } from '../data/EstruturaOrganizacional';

// Componente para uma bolinha individual animada
const FloatingDot = ({ index, isPrimary }) => {
  const animatedValue = useRef(new Animated.Value(0)).current;
  const opacityValue = useRef(new Animated.Value(0.4)).current;
  const scaleValue = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const startAnimation = () => {
      // Reset das posições
      animatedValue.setValue(0);
      opacityValue.setValue(0.4);
      scaleValue.setValue(1);

      Animated.parallel([
        // Movimento para cima
        Animated.timing(animatedValue, {
          toValue: -120, // Sobe 120 pixels
          duration: 12000 + (index % 4) * 2000, // Duração variável
          useNativeDriver: true,
        }),
        // Mudança de opacidade
        Animated.sequence([
          Animated.timing(opacityValue, {
            toValue: 0.8,
            duration: 2000,
            useNativeDriver: true,
          }),
          Animated.timing(opacityValue, {
            toValue: 0,
            duration: 8000 + (index % 4) * 1000,
            useNativeDriver: true,
          }),
        ]),
        // Mudança de escala
        Animated.sequence([
          Animated.timing(scaleValue, {
            toValue: 1.5,
            duration: 6000,
            useNativeDriver: true,
          }),
          Animated.timing(scaleValue, {
            toValue: 0.5,
            duration: 6000 + (index % 4) * 1000,
            useNativeDriver: true,
          }),
        ]),
      ]).start(() => {
        // Reinicia a animação quando termina
        setTimeout(() => startAnimation(), (index % 3) * 1000 + 2000);
      });
    };

    // Delay inicial escalonado para cada bolinha
    const initialDelay = setTimeout(() => {
      startAnimation();
    }, index * 1500);

    return () => clearTimeout(initialDelay);
  }, [index, animatedValue, opacityValue, scaleValue]);

  return (
    <Animated.View
      style={[
        styles.dot,
        isPrimary ? styles.dotPrimary : styles.dotSecondary,
        {
          left: `${2 + index * 7.7}%`, // Começar mais à esquerda e espaçamento ajustado
          bottom: 10,
          transform: [{ translateY: animatedValue }, { scale: scaleValue }],
          opacity: opacityValue,
        },
      ]}
    />
  );
};

// Componente de Simulação de Usuário - CORRIGIDO
const UserSimulationComponent = ({
  isSimulating,
  simulatedUser,
  canSimulate,
  onStartSimulation,
  onStopSimulation,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Debug log
  console.log('🎭 UserSimulationComponent:', {
    canSimulate,
    isSimulating,
    simulatedUser: simulatedUser?.nome,
  });

  // Filtrar avaliadores baseado na busca
  // Filtrar avaliadores baseado na busca E ORDENAR ALFABETICAMENTE
  const filteredAvaliadores = AVALIADORES.filter(
    av =>
      !av.isSpecial &&
      (av.nome.toLowerCase().includes(searchText.toLowerCase()) ||
        av.login.toLowerCase().includes(searchText.toLowerCase()))
  ).sort((a, b) => {
    const normalizeString = str => {
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    };
    const nomeA = normalizeString(a.nome);
    const nomeB = normalizeString(b.nome);
    return nomeA.localeCompare(nomeB);
  });

  if (!canSimulate && !isSimulating) {
    return null;
  }

  // Se está simulando, mostrar banner e botão de parar
  if (isSimulating && simulatedUser) {
    return (
      <View style={styles.simulationBanner}>
        <MaterialCommunityIcons name="account-eye" size={20} color="#FF6F00" />
        <RNText style={styles.simulationText}>
          Visualizando como: {simulatedUser.nome.split(' ').slice(0, 2).join(' ')}
        </RNText>
        <TouchableOpacity style={styles.stopSimulationButton} onPress={onStopSimulation}>
          <MaterialCommunityIcons name="close-circle" size={24} color="#FF6F00" />
        </TouchableOpacity>
      </View>
    );
  }

  // Botão para iniciar simulação com menu customizado (sem usar Menu da react-native-paper)
  return (
    <>
      <TouchableOpacity style={styles.simulateButton} onPress={() => setMenuVisible(true)}>
        <MaterialCommunityIcons name="account-eye" size={18} color="#FFFFFF" />
        <RNText style={styles.simulateButtonText}>Simular</RNText>
      </TouchableOpacity>

      {/* Modal customizado em vez de Menu */}
      <Modal
        visible={menuVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setMenuVisible(false);
          setSearchText('');
        }}
      >
        <TouchableOpacity
          style={styles.menuOverlay}
          activeOpacity={1}
          onPress={() => {
            setMenuVisible(false);
            setSearchText('');
          }}
        >
          <View style={styles.menuContainer} onStartShouldSetResponder={() => true}>
            <View style={styles.menuHeader}>
              <RNText style={styles.menuTitle}>Simular Visualização</RNText>
              <View style={styles.searchContainer}>
                <MaterialCommunityIcons name="magnify" size={20} color="#666" />
                <TextInput
                  style={styles.searchInput}
                  onChangeText={setSearchText}
                  placeholder="Buscar membro..."
                  value={searchText}
                  placeholderTextColor="#999"
                />
              </View>
            </View>

            <ScrollView style={styles.menuScroll}>
              {filteredAvaliadores.map(avaliador => (
                <TouchableOpacity
                  key={avaliador.id}
                  style={styles.menuItem}
                  onPress={() => {
                    onStartSimulation(avaliador);
                    setMenuVisible(false);
                    setSearchText('');
                  }}
                >
                  <View style={styles.menuItemContent}>
                    <RNText style={styles.menuItemName}>{avaliador.nome}</RNText>
                    <RNText style={styles.menuItemLevel}>
                      {getLevelEmoji(avaliador.level)} {getLevelDescription(avaliador.level)}
                    </RNText>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

export default function HeaderComponent({
  currentScreen,
  onBackPress,
  serverStatus,
  syncStatus,
  onSyncPress,
  currentUser,
  onLogout,
  onOpenLogin,
  canCreateForm,
  canSimulate,
  isSimulating,
  simulatedUser,
  onStartSimulation,
  onStopSimulation,
  onCreateForm,
  onViewResults,
  canViewResults,
  canValidate,
  onValidate,
}) {
  const [userModalVisible, setUserModalVisible] = useState(false);

  // ADICIONE ESTA VERIFICAÇÃO NO INÍCIO:
  // Se currentUser for null, criar um objeto visitante padrão
  const safeCurrentUser = currentUser || {
    id: 'visitor',
    nome: 'Visitante',
    email: 'visitante@avaqualis.com',
    level: 'visitor',
  };

  // Debug log detalhado - ATUALIZADO
  console.log('🎯 HeaderComponent props:', {
    currentUser: safeCurrentUser?.nome,
    currentUserLevel: safeCurrentUser?.level,
    currentUserId: safeCurrentUser?.id,
    canCreateForm,
    canSimulate,
    isSimulating,
    onCreateForm: !!onCreateForm,
  });

  const getServerStatusIcon = () => {
    switch (serverStatus) {
      case 'online':
        return 'cloud-check';
      case 'offline':
        return 'cloud-off-outline';
      case 'checking':
        return 'cloud-sync';
      default:
        return 'cloud-alert';
    }
  };

  const getServerStatusColor = () => {
    switch (serverStatus) {
      case 'online':
        return '#4CAF50';
      case 'offline':
        return '#F44336';
      case 'checking':
        return '#FF9800';
      default:
        return '#9E9E9E';
    }
  };

  const handleLogoutPress = () => {
    console.log('🔴 Botão de logout pressionado');

    if (currentUser?.id === 'visitor') {
      console.log('❌ Usuário é visitante, não pode fazer logout');
      return;
    }

    // Fechar o modal primeiro
    setUserModalVisible(false);

    // Usar setTimeout para garantir que o modal feche antes de mostrar o Alert
    setTimeout(() => {
      if (Platform.OS === 'web') {
        // Para web, usar confirm nativo
        const confirmed = window.confirm(
          'Tem certeza que deseja sair? Todos os dados locais serão apagados.'
        );
        if (confirmed) {
          console.log('✅ Logout confirmado via window.confirm');
          if (onLogout) {
            onLogout();
          }
        }
      } else {
        // Para mobile, usar Alert do React Native
        Alert.alert(
          'Confirmar Logout',
          'Tem certeza que deseja sair? Todos os dados locais serão apagados.',
          [
            {
              text: 'Cancelar',
              style: 'cancel',
              onPress: () => console.log('Logout cancelado'),
            },
            {
              text: 'Sair',
              style: 'destructive',
              onPress: () => {
                console.log('✅ Confirmado logout via Alert');
                if (onLogout) {
                  onLogout();
                } else {
                  console.error('❌ Função onLogout não está definida!');
                }
              },
            },
          ],
          { cancelable: true }
        );
      }
    }, 100);
  };

  // Obter usuário efetivo (simulado ou real)
  const effectiveUser = isSimulating && simulatedUser ? simulatedUser : safeCurrentUser;

  return (
    <>
      <View style={styles.header} role="banner">
        {/* Linha verde no topo como no Quizito */}
        <View style={styles.topGreenLine} />

        {/* Decorações de fundo melhoradas */}
        <View style={styles.decorationLeft} />
        <View style={styles.decorationRight} />
        <View style={styles.decorationCenter} />

        {/* Pontos flutuantes animados - agora cobrindo melhor a largura */}
        <View style={styles.floatingDots}>
          {Array.from({ length: 13 }).map(
            (
              _,
              i // Aumentei para 13 para cobrir toda a largura
            ) => (
              <FloatingDot key={i} index={i} isPrimary={i % 2 === 0} />
            )
          )}
        </View>

        <View style={styles.headerContainer}>
          <View style={styles.titleRow}>
            <View style={styles.logoContainer}>
              <Image
                source={require('../../assets/avaqualis-logo.png')}
                style={styles.logo}
                resizeMode="contain"
                alt="Logo AVAQualis.com - Protótipo de Ferramenta Mobile para Programa AVAQualis"
                accessibilityLabel="Logo AVAQualis.com - Protótipo de Ferramenta Mobile para avaliação do programa AVAQualis no HC-UFU/EBSERH"
              />
            </View>

            <View style={styles.titleContainer}>
              <View style={styles.titleTextRow}>
                <Text style={styles.titleDecorationLeft}>✧</Text>
                <Title style={styles.appTitle} role="heading" aria-level={1}>
                  <Text style={styles.titlePrimary}>AVAQ</Text>
                  <Text style={styles.titleSecondary}>ualis.com</Text>
                </Title>
                <Text style={styles.titleDecorationRight}>✧</Text>
              </View>

              <Text style={styles.appSubtitle} role="text">
                Protótipo de Ferramenta Mobile para auxiliar {'\n'}na avaliação do programa
                AVAQualis no HC-UFU/EBSERH{'\n'}
                <Text style={styles.partnershipTag}>
                  Desenvolvido por:
                  <Text style={styles.partnershipHighlight}> 🚀 UGITS </Text>+
                  <Text style={styles.partnershipHighlight}> 📋 UGQ</Text>
                </Text>
              </Text>
            </View>
          </View>
        </View>

        {/* Barra de controles no canto superior direito */}
        <View style={styles.controlsBar}>
          {/* Componente de Simulação */}
          <UserSimulationComponent
            isSimulating={isSimulating}
            simulatedUser={simulatedUser}
            canSimulate={canSimulate}
            onStartSimulation={onStartSimulation}
            onStopSimulation={onStopSimulation}
          />

          {/* Botão Criar Formulário - Apenas para usuários com permissão */}
          {canCreateForm && onCreateForm && (
            <TouchableOpacity onPress={onCreateForm} style={styles.createFormButton}>
              <RNText style={styles.createFormButtonText}>Criar</RNText>
              <MaterialCommunityIcons name="plus-circle" size={18} color="#FFFFFF" />
              <MaterialCommunityIcons
                name="file-document-edit"
                size={16}
                color="#FFFFFF"
                style={{ marginLeft: 3 }}
              />
            </TouchableOpacity>
          )}

          {/* Botão Resultados - Apenas para coordenação e programadores */}
          {canViewResults && onViewResults && (
            <TouchableOpacity onPress={onViewResults} style={styles.resultsButton}>
              <MaterialCommunityIcons name="chart-box" size={18} color="#FFFFFF" />
              <RNText style={styles.resultsButtonText}>Resultados</RNText>
            </TouchableOpacity>
          )}

          {/* Botão Validação - Apenas para coordenação e programadores */}
          {canValidate && onValidate && (
            <TouchableOpacity onPress={onValidate} style={styles.validationButton}>
              <MaterialCommunityIcons
                name="checkbox-marked-circle-outline"
                size={18}
                color="#FFFFFF"
              />
              <RNText style={styles.validationButtonText}>Validação</RNText>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={[styles.loginButton, safeCurrentUser?.id !== 'visitor' && styles.loggedInButton]}
            onPress={
              safeCurrentUser?.id === 'visitor' ? onOpenLogin : () => setUserModalVisible(true)
            }
          >
            <MaterialCommunityIcons
              name={safeCurrentUser?.id === 'visitor' ? 'login' : 'account-check'}
              size={20}
              color="#FFFFFF"
            />
            <RNText style={styles.loginButtonText}>
              {safeCurrentUser?.id === 'visitor' ? 'Login' : safeCurrentUser.nome.split(' ')[0]}
            </RNText>
          </TouchableOpacity>

          {/* Indicador de Status do Servidor */}
          <View style={styles.statusIndicator}>
            <MaterialCommunityIcons
              name={getServerStatusIcon()}
              size={24}
              color={getServerStatusColor()}
            />
            {syncStatus?.isSyncing && <Badge size={10} style={styles.syncBadge} />}
          </View>
        </View>
      </View>

      {/* Modal de Informações do Usuário */}
      <Modal
        visible={userModalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setUserModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setUserModalVisible(false)}
        >
          <View style={styles.modalContent} onStartShouldSetResponder={() => true}>
            <View style={styles.modalHeader}>
              <RNText style={styles.modalTitle}>Informações do Usuário</RNText>
              <IconButton icon="close" size={24} onPress={() => setUserModalVisible(false)} />
            </View>

            <ScrollView style={styles.modalBody}>
              {/* Indicador de Simulação */}
              {isSimulating && simulatedUser && (
                <View style={styles.simulationInfo}>
                  <MaterialCommunityIcons name="account-eye" size={20} color="#FF6F00" />
                  <RNText style={styles.simulationInfoText}>
                    Você está visualizando o sistema como: {simulatedUser.nome}
                  </RNText>
                </View>
              )}

              {/* Avatar */}
              <View style={styles.avatarSection}>
                <View
                  style={[
                    styles.avatar,
                    effectiveUser?.id === 'visitor' && styles.avatarVisitor,
                    isSimulating && styles.avatarSimulating,
                  ]}
                >
                  <RNText style={styles.avatarText}>{effectiveUser?.nome?.charAt(0) || '?'}</RNText>
                </View>
                {isSimulating && <RNText style={styles.simulatingLabel}>Modo Simulação</RNText>}
              </View>

              {/* Informações do Usuário */}
              <List.Section>
                <List.Item
                  title="Nome"
                  description={effectiveUser?.nome || 'Não informado'}
                  left={() => <List.Icon icon="account" />}
                />

                {effectiveUser?.id !== 'visitor' && (
                  <>
                    <List.Item
                      title="Email"
                      description={effectiveUser?.email || effectiveUser?.login || 'Não informado'}
                      left={() => <List.Icon icon="email" />}
                    />

                    <List.Item
                      title="Nível"
                      description={`${getLevelEmoji(effectiveUser?.level)} ${getLevelDescription(effectiveUser?.level)}`}
                      left={() => <List.Icon icon="shield-account" />}
                    />
                  </>
                )}

                {/* Informações do usuário real se estiver simulando */}
                {isSimulating && currentUser && (
                  <>
                    <Divider style={{ marginVertical: 8 }} />
                    <List.Subheader>Usuário Real</List.Subheader>
                    <List.Item
                      title="Nome"
                      description={currentUser.nome}
                      left={() => <List.Icon icon="account-circle" />}
                    />
                    <List.Item
                      title="Nível"
                      description={`${getLevelEmoji(currentUser.level)} ${getLevelDescription(currentUser.level)}`}
                      left={() => <List.Icon icon="shield-account" />}
                    />
                  </>
                )}

                <List.Item
                  title="Status da Conexão"
                  description={
                    serverStatus === 'online'
                      ? 'Conectado ao servidor'
                      : serverStatus === 'offline'
                        ? 'Modo offline'
                        : 'Verificando conexão...'
                  }
                  left={() => (
                    <List.Icon icon={getServerStatusIcon()} color={getServerStatusColor()} />
                  )}
                />
              </List.Section>

              {/* Informações Adicionais para Visitantes */}
              {effectiveUser?.id === 'visitor' && (
                <View style={styles.visitorInfo}>
                  <MaterialCommunityIcons name="information" size={20} color="#FF9800" />
                  <RNText style={styles.visitorInfoText}>
                    Você está em modo visitante. Pode visualizar formulários e criar respostas
                    localmente, mas não pode enviar dados para o servidor.
                  </RNText>
                </View>
              )}
            </ScrollView>

            {/* Botões no Footer */}
            <View style={styles.modalFooter}>
              {/* Botão para parar simulação se estiver ativa */}
              {isSimulating && (
                <TouchableOpacity
                  style={styles.stopSimulationModalButton}
                  onPress={() => {
                    setUserModalVisible(false);
                    onStopSimulation();
                  }}
                >
                  <MaterialCommunityIcons name="account-eye-outline" size={20} color="#FFFFFF" />
                  <RNText style={styles.stopSimulationModalButtonText}>Parar Simulação</RNText>
                </TouchableOpacity>
              )}

              {/* Botão de Login ou Logout */}
              {currentUser?.id === 'visitor' ? (
                <TouchableOpacity
                  style={styles.loginModalButton}
                  onPress={() => {
                    setUserModalVisible(false);
                    onOpenLogin();
                  }}
                >
                  <MaterialCommunityIcons name="login" size={20} color="#FFFFFF" />
                  <RNText style={styles.loginModalButtonText}>Fazer Login</RNText>
                </TouchableOpacity>
              ) : (
                !isSimulating && (
                  <TouchableOpacity style={styles.logoutButton} onPress={handleLogoutPress}>
                    <MaterialCommunityIcons name="logout" size={20} color="#FFFFFF" />
                    <RNText style={styles.logoutButtonText}>Sair da Conta</RNText>
                  </TouchableOpacity>
                )
              )}
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  // Adicione os estilos no final do arquivo:
  resultsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#673AB7', // Cor roxa para diferenciar
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    ...AVAQualisTheme.shadows.small,
  },
  resultsButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 3,
    borderBottomColor: AVAQualisTheme.colors.primary,
    position: 'relative',
    overflow: 'hidden',
    minHeight: 120,
    justifyContent: 'center',
    ...AVAQualisTheme.shadows.medium,
  },
  topGreenLine: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: AVAQualisTheme.colors.primary,
    zIndex: 3,
  },
  decorationLeft: {
    position: 'absolute',
    top: -40,
    left: -40,
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: `${AVAQualisTheme.colors.primary}06`,
  },
  decorationRight: {
    position: 'absolute',
    bottom: -30,
    right: -30,
    width: 90,
    height: 90,
    borderRadius: 45,
    backgroundColor: `${AVAQualisTheme.colors.secondary}06`,
  },
  decorationCenter: {
    position: 'absolute',
    top: 20,
    right: '25%',
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: `${AVAQualisTheme.colors.primary}04`,
  },
  floatingDots: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    zIndex: 1,
    pointerEvents: 'none',
  },
  dot: {
    position: 'absolute',
    width: 5,
    height: 5,
    borderRadius: 2.5,
  },
  dotPrimary: {
    backgroundColor: AVAQualisTheme.colors.primary,
    shadowColor: AVAQualisTheme.colors.primary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 3,
    elevation: 2,
  },
  dotSecondary: {
    backgroundColor: AVAQualisTheme.colors.secondary,
    shadowColor: AVAQualisTheme.colors.secondary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.6,
    shadowRadius: 2,
    elevation: 1,
  },
  headerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoContainer: {
    marginRight: 12,
    alignItems: 'center',
  },
  logo: {
    width: 50,
    height: 55,
  },
  titleContainer: {
    alignItems: 'center',
    flex: 1,
  },
  titleTextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  titleDecorationLeft: {
    fontSize: 20,
    color: AVAQualisTheme.colors.primary,
    marginRight: 8,
    opacity: 0.9,
    textShadowColor: `${AVAQualisTheme.colors.primary}40`,
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  titleDecorationRight: {
    fontSize: 20,
    color: AVAQualisTheme.colors.secondary,
    marginLeft: 8,
    opacity: 0.9,
    textShadowColor: `${AVAQualisTheme.colors.secondary}40`,
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  appTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    marginBottom: 0,
  },
  titlePrimary: {
    color: AVAQualisTheme.colors.primary,
    textShadowColor: `${AVAQualisTheme.colors.primary}30`,
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  titleSecondary: {
    color: AVAQualisTheme.colors.secondary,
    textShadowColor: `${AVAQualisTheme.colors.secondary}30`,
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  appSubtitle: {
    fontSize: 10,
    color: AVAQualisTheme.colors.tertiary,
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    maxWidth: '90%',
    ...AVAQualisTheme.shadows.small,
  },
  partnershipTag: {
    fontSize: 9,
    color: AVAQualisTheme.colors.tertiary,
    textAlign: 'center',
    maxWidth: '95%',
  },
  partnershipHighlight: {
    color: AVAQualisTheme.colors.primary,
    fontWeight: 'bold',
  },
  // Novos estilos para autenticação
  controlsBar: {
    position: 'absolute',
    top: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 4,
  },
  createFormButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AVAQualisTheme.colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    ...AVAQualisTheme.shadows.small,
  },
  createFormButtonText: {
    color: AVAQualisTheme.colors.onPrimary,
    fontSize: 12,
    marginRight: 6,
  },
  loginButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AVAQualisTheme.colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    ...AVAQualisTheme.shadows.small,
  },
  loggedInButton: {
    backgroundColor: '#4CAF50',
  },
  loginButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  statusIndicator: {
    position: 'relative',
  },
  syncBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#FF9800',
  },
  // Estilos de Simulação
  simulationBanner: {
    position: 'absolute',
    top: 60,
    left: 16,
    right: 16,
    backgroundColor: '#FFF3E0',
    borderRadius: 8,
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 5,
    elevation: 5,
    borderWidth: 1,
    borderColor: '#FF9800',
  },
  simulationText: {
    flex: 1,
    marginLeft: 8,
    fontSize: 12,
    color: '#E65100',
    fontWeight: '600',
  },
  stopSimulationButton: {
    padding: 4,
  },
  simulateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF9800',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    ...AVAQualisTheme.shadows.small,
  },
  simulateButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  // Estilos do Menu customizado
  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    maxHeight: 400,
    minWidth: 300,
    maxWidth: '90%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  menuHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  menuTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.primary,
    marginBottom: 12,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    color: '#333',
  },
  menuScroll: {
    maxHeight: 300,
  },
  menuItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  menuItemContent: {
    flexDirection: 'column',
  },
  menuItemName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  menuItemLevel: {
    fontSize: 12,
    color: '#666',
  },
  // Estilos do Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.primary,
  },
  modalBody: {
    paddingBottom: 16,
  },
  simulationInfo: {
    flexDirection: 'row',
    backgroundColor: '#FFF3E0',
    padding: 16,
    margin: 16,
    borderRadius: 8,
    alignItems: 'flex-start',
    borderLeftWidth: 4,
    borderLeftColor: '#FF9800',
  },
  simulationInfoText: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    color: '#E65100',
    lineHeight: 20,
    fontWeight: '600',
  },
  avatarSection: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: AVAQualisTheme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarVisitor: {
    backgroundColor: '#FF9800',
  },
  avatarSimulating: {
    borderWidth: 3,
    borderColor: '#FF6F00',
  },
  avatarText: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  simulatingLabel: {
    marginTop: 8,
    fontSize: 12,
    color: '#FF6F00',
    fontWeight: 'bold',
  },
  visitorInfo: {
    flexDirection: 'row',
    backgroundColor: '#FFF3E0',
    padding: 16,
    margin: 16,
    borderRadius: 8,
    alignItems: 'flex-start',
  },
  visitorInfoText: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    color: '#E65100',
    lineHeight: 20,
  },
  modalFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    gap: 8,
  },
  loginModalButton: {
    flexDirection: 'row',
    backgroundColor: AVAQualisTheme.colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginModalButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  stopSimulationModalButton: {
    flexDirection: 'row',
    backgroundColor: '#FF9800',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stopSimulationModalButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  logoutButton: {
    flexDirection: 'row',
    backgroundColor: '#F44336',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoutButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },

  validationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF9800', // Cor laranja para validação
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    ...AVAQualisTheme.shadows.small,
  },
  validationButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
});
